import { Injectable, OnDestroy } from '@angular/core';
import { HardNavigationService } from './hard-navigation.service';
import { AccountRoleType, ROLE_META, jwtClaims } from './saved-accounts.model';

/** Login pages: not "inside a cabinet", so FR-28 leaves them alone. `/owner` and
 * `/marketolog` (exact) render the login form too — their routing modules map `''` to it. */
const LOGIN_PATHS = new Set([
  '/auth',
  '/owner',
  '/owner-login',
  '/marketolog',
  '/marketolog-login',
]);

/**
 * FR-28: which role's cabinet a path belongs to, or `null` on a login page.
 *
 * Deviation from the PLAN (stricter): `/owner-login/…` and `/marketolog-login/…` sub-paths map
 * to their role, not `null`. Both prefixes load the full role module (e.g. `/owner-login/reports`
 * renders the reports page), and the role interceptors pick the token by
 * `pathname.startsWith('/owner' | '/marketolog')`, so such a tab does act with that role's key.
 */
export const roleOfPath = (path: string): AccountRoleType | null => {
  const p = path.length > 1 ? path.replace(/\/+$/, '') : path;
  if (LOGIN_PATHS.has(p) || p.startsWith('/auth/')) {
    return null;
  }
  if (p.startsWith('/owner/') || p.startsWith('/owner-login/')) {
    return 'OWNER';
  }
  if (p.startsWith('/marketolog/') || p.startsWith('/marketolog-login/')) {
    return 'MARKETOLOG';
  }
  return 'ORGANIZER';
};

/** The lower-cased access-JWT `user_name` of a raw role-key value, or `null` when absent or
 * unparseable. */
export const identityOf = (raw: string | null): string | null => {
  if (!raw) {
    return null;
  }
  try {
    const token = (JSON.parse(raw) as { token?: unknown } | null)?.token;
    if (typeof token !== 'string') {
      return null;
    }
    const userName = jwtClaims(token)?.user_name;
    return typeof userName === 'string' && userName.trim() ? userName.trim().toLowerCase() : null;
  } catch {
    return null;
  }
};

/**
 * FR-28 cross-tab consistency. The browser fires `storage` only in the OTHER tabs of the origin,
 * so a switch / logout / logout-all in one tab full-loads every other tab sitting inside the same
 * role's cabinet: to the landing route when a session remains, else to the role's login page
 * (which shows the chooser). A token refresh of the same account (identical `user_name`) is
 * ignored, and tabs of other roles and on login pages are unaffected.
 */
@Injectable({ providedIn: 'root' })
export class AccountTabSyncService implements OnDestroy {
  private started = false;

  constructor(private hardNav: HardNavigationService) {}

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    window.addEventListener('storage', this.onStorage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorage);
    this.started = false;
  }

  readonly onStorage = (e: StorageEvent): void => {
    if (e.storageArea && e.storageArea !== localStorage) {
      return;
    }
    const role = roleOfPath(this.hardNav.pathname());
    if (role === null) {
      return;
    }
    const meta = ROLE_META[role];
    if (e.key !== null && e.key !== meta.roleKey) {
      return;
    }
    const now = localStorage.getItem(meta.roleKey);
    if (!now) {
      this.hardNav.assign(meta.loginPath);
      return;
    }
    const before = identityOf(e.key === null ? null : e.oldValue);
    if (before !== null && before === identityOf(now)) {
      return;
    }
    this.hardNav.assign(meta.landingRoute);
  };
}
